/*
  assets/utopia-mech16.js
  Mech 16 landing page. Small, dependency free, loaded once (deferred) by
  sections/mech16-assets.liquid. Does nothing on any other page.

  1. Heading reveals. Headings are only hidden once this script runs (it adds
     .m16-motion), so with no JavaScript everything is simply visible.
     Reduced motion skips it.
  2. GPU selector (sections/mech16-gpu). Without JavaScript the three GPUs
     show side by side. With it, they become an accessible tab set.
  3. Gallery (sections/mech16-gallery). Rail arrows and a <dialog> viewer.
  4. Scroll video (sections/mech16-scroll-video). Scroll position drives an
     image sequence (or video) frame by frame, both directions.
     PINNING is verified live, not assumed. Three modes, tried in order:
       sticky  CSS position: sticky (smoothest, compositor driven)
       fixed   position: fixed while pinned, switched only at the start and
               end of the sequence (used if sticky is found not to hold)
       shift   the stage is moved with a transform on every scroll event
               (last resort, only if the theme also defeats fixed)
     BLACK MATCHING: with "Blend the black background" on, each frame is
     painted onto the page's own background colour with a 'lighten'
     composite, so the frames' pure black becomes exactly the page black.
     Reduced motion: never runs (static still + captions).
  5. Configurator on demand (sections/mech16-routes). Same rules as the
     desktop pages' rn-config-loader.js, written separately because the
     laptop has two configurators and a different configurator snippet:
       - nothing is fetched until a real click; crawlers never trigger it;
       - the configurator comes from <configurator page>?view=laptopconf
         (templates/page.laptopconf.liquid); theme previews keep working
         because preview_theme_id is copied across;
       - scripts in the fetched markup are re-run in order; jQuery itself is
         skipped so the page's existing jQuery (and its handlers) survive;
       - it only ever runs once per page, so switching platform after one
         has opened stores the choice and reloads, then opens the other;
       - if anything fails, the visitor is sent to the standalone
         configurator page, so a sale is never lost.
  6. Theme Editor: sections re-rendered by the editor are set up again, and
     selecting a gallery block scrolls to that photograph.
  Later stages add the sticky nav here.
*/
(function () {
  'use strict';

  if (!document.querySelector('.m16')) return;

  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };
  var clamp = function (v, a, b) { return Math.min(Math.max(v, a), b); };
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. heading reveals ---------------------------------------------- */
  var targets = document.querySelectorAll('.m16 [data-m16-reveal]');
  if (!reduce && 'IntersectionObserver' in window && targets.length) {
    each(document.querySelectorAll('.m16'), function (r) { r.classList.add('m16-motion'); });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    each(targets, function (el) { io.observe(el); });
  }

  /* ---- 2. tab sets (GPU selector) --------------------------------------- */
  function initTabs(set) {
    if (set.hasAttribute('data-m16-ready')) return;
    var list = set.querySelector('[data-m16-tablist]');
    var tabs = set.querySelectorAll('[data-m16-tab]');
    var panels = set.querySelectorAll('[data-m16-panel]');
    if (!list || !tabs.length || tabs.length !== panels.length) return;
    set.setAttribute('data-m16-ready', '');

    list.hidden = false;
    list.setAttribute('role', 'tablist');
    set.classList.add('is-tabbed');

    each(tabs, function (tab, i) {
      var panel = panels[i];
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.setAttribute('tabindex', '0');
    });

    function select(index, focus) {
      each(tabs, function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        panels[i].hidden = !on;
      });
      if (focus) tabs[index].focus();
    }

    var start = 0;
    each(tabs, function (tab, i) {
      if (tab.hasAttribute('data-m16-default')) start = i;
      tab.addEventListener('click', function () { select(i, false); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = tabs.length - 1;
        if (next !== null) { e.preventDefault(); select(next, true); }
      });
    });
    select(start, false);
  }

  /* ---- 3. gallery ------------------------------------------------------- */
  function initGallery(gal) {
    if (gal.hasAttribute('data-m16-ready')) return;
    var rail = gal.querySelector('[data-m16-gal-rail]');
    var links = gal.querySelectorAll('[data-m16-gal-open]');
    if (!rail) return;
    gal.setAttribute('data-m16-ready', '');

    var controls = gal.querySelector('[data-m16-gal-controls]');
    if (controls) {
      var prev = controls.querySelector('[data-m16-gal-prev]');
      var next = controls.querySelector('[data-m16-gal-next]');
      var update = function () {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        controls.hidden = max <= 0;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      };
      var go = function (dir) {
        rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', function () { go(-1); });
      next.addEventListener('click', function () { go(1); });
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      each(rail.querySelectorAll('img'), function (img) { img.addEventListener('load', update); });
      update();
    }

    var dlg = gal.querySelector('[data-m16-lightbox]');
    if (!dlg || !links.length || typeof dlg.showModal !== 'function') return;
    var big = dlg.querySelector('[data-m16-lb-img]');
    var cap = dlg.querySelector('[data-m16-lb-cap]');
    var inner = dlg.querySelector('[data-m16-lb-inner]');
    var current = 0;
    var opener = null;

    function show(i) {
      current = (i + links.length) % links.length;
      var a = links[current];
      var thumb = a.querySelector('img');
      big.src = a.getAttribute('href');
      big.alt = thumb ? thumb.alt : '';
      var text = a.getAttribute('data-caption') || '';
      cap.textContent = text;
      cap.hidden = !text;
    }

    each(links, function (a, i) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        opener = a;
        show(i);
        dlg.showModal();
      });
    });

    dlg.querySelector('[data-m16-lb-close]').addEventListener('click', function () { dlg.close(); });
    var lbPrev = dlg.querySelector('[data-m16-lb-prev]');
    var lbNext = dlg.querySelector('[data-m16-lb-next]');
    if (lbPrev) lbPrev.addEventListener('click', function () { show(current - 1); });
    if (lbNext) lbNext.addEventListener('click', function () { show(current + 1); });

    dlg.addEventListener('keydown', function (e) {
      if (links.length < 2) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1); }
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target === inner) dlg.close();
    });
    dlg.addEventListener('close', function () {
      big.src = 'data:,';
      if (opener) opener.focus();
    });
  }

  /* ---- 4. scroll video -------------------------------------------------- */
  function stickyBlocked(el) {
    for (var p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
      var cs = window.getComputedStyle(p);
      if (/(hidden|auto|scroll)/.test(cs.overflowY + ' ' + cs.overflowX)) return true;
    }
    return false;
  }

  function initScrollVideo(sec) {
    if (sec.hasAttribute('data-m16-ready')) return;
    sec.setAttribute('data-m16-ready', '');
    if (reduce || !('IntersectionObserver' in window)) return;

    var track = sec.querySelector('.m16-sv__track');
    var stage = sec.querySelector('.m16-sv__stage');
    var video = sec.querySelector('[data-m16-sv-video]');
    var canvas = sec.querySelector('[data-m16-sv-canvas]');
    if (!track || !stage || (!video && !canvas)) return;

    var caps = sec.querySelectorAll('.m16-sv__cap');
    var smooth = clamp(parseFloat(sec.getAttribute('data-smooth')) || 0, 0, 0.95);
    var hold = clamp(parseFloat(sec.getAttribute('data-hold')) || 0, 0, 0.5);
    var blend = sec.classList.contains('m16-sv--blend');
    var target = 0, current = -1, visible = false, loaded = false, raf = 0;
    var duration = 0, frames = [], images = [], ctx = null, lastDrawn = -1;
    var mode = 'sticky';
    var pageColour = '';

    sec.classList.add('is-live');

    function getPageColour() {
      if (!pageColour) {
        var host = sec.closest('.m16') || sec;
        var c = window.getComputedStyle(host).backgroundColor;
        pageColour = (!c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)') ? '#080908' : c;
      }
      return pageColour;
    }

    function measure() {
      var r = track.getBoundingClientRect();
      var total = r.height - window.innerHeight;
      return { p: total > 0 ? clamp(-r.top / total, 0, 1) : 0, top: r.top, total: Math.max(total, 0) };
    }

    function place() {
      var m = measure();
      if (mode === 'fixed') {
        var pinned = m.top <= 0 && -m.top < m.total;
        var after = m.total > 0 && -m.top >= m.total;
        if (sec.classList.contains('is-pinned') !== pinned) sec.classList.toggle('is-pinned', pinned);
        if (sec.classList.contains('is-after') !== after) sec.classList.toggle('is-after', after);
      } else if (mode === 'shift') {
        stage.style.transform = 'translate3d(0,' + clamp(-m.top, 0, m.total) + 'px,0)';
      }
      return m;
    }

    function setMode(next) {
      mode = next;
      sec.classList.toggle('is-nosticky', mode !== 'sticky');
      sec.classList.toggle('is-shift', mode === 'shift');
      if (mode !== 'fixed') { sec.classList.remove('is-pinned'); sec.classList.remove('is-after'); }
      if (mode !== 'shift') stage.style.transform = '';
      place();
    }

    function verify(m) {
      if (mode === 'shift' || m.total <= 0 || m.p < 0.05 || m.p > 0.95) return;
      if (Math.abs(stage.getBoundingClientRect().top) <= 2) return;
      setMode(mode === 'sticky' ? 'fixed' : 'shift');
    }

    if (stickyBlocked(track)) setMode('fixed');

    function sizeCanvas() {
      if (!canvas) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(canvas.clientWidth * dpr);
      var h = Math.round(canvas.clientHeight * dpr);
      if (w && h && (canvas.width !== w || canvas.height !== h)) {
        canvas.width = w;
        canvas.height = h;
        lastDrawn = -1;
      }
    }

    function drawFrame(p) {
      if (!ctx || !frames.length) return;
      var want = Math.round(p * (frames.length - 1));
      var idx = -1;
      for (var d = 0; d < frames.length; d++) {
        if (images[want - d]) { idx = want - d; break; }
        if (images[want + d]) { idx = want + d; break; }
      }
      if (idx < 0 || idx === lastDrawn) return;
      var img = images[idx];
      var cw = canvas.width, ch = canvas.height;
      var s = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
      var w = img.naturalWidth * s, h = img.naturalHeight * s;
      ctx.globalCompositeOperation = 'source-over';
      if (blend) {
        ctx.fillStyle = getPageColour();
        ctx.fillRect(0, 0, cw, ch);
        ctx.globalCompositeOperation = 'lighten';
      } else {
        ctx.clearRect(0, 0, cw, ch);
      }
      ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      ctx.globalCompositeOperation = 'source-over';
      lastDrawn = idx;
    }

    function load() {
      if (loaded) return;
      loaded = true;
      var small = window.matchMedia('(max-width: 820px)').matches;
      if (video) {
        video.src = (small && video.getAttribute('data-src-small')) || video.getAttribute('data-src');
        video.preload = 'auto';
        video.addEventListener('loadedmetadata', function () { duration = video.duration || 0; current = -1; tick(); });
        video.addEventListener('seeked', tick);
        var primed = video.play();
        if (primed && primed.then) primed.then(function () { video.pause(); tick(); }).catch(function () {});
      } else {
        var attr = (small && canvas.getAttribute('data-frames-small')) || canvas.getAttribute('data-frames');
        try { frames = JSON.parse(attr) || []; } catch (e) { frames = []; }
        ctx = canvas.getContext('2d');
        sizeCanvas();
        var seen = {};
        [8, 4, 2, 1].forEach(function (step) {
          for (var i = 0; i < frames.length; i += step) {
            if (seen[i]) continue;
            seen[i] = true;
            (function (n) {
              var img = new Image();
              img.decoding = 'async';
              img.src = frames[n];
              var ready = img.decode ? img.decode() : new Promise(function (res, rej) { img.onload = res; img.onerror = rej; });
              ready.then(function () { images[n] = img; lastDrawn = -1; tick(); }).catch(function () {});
            })(i);
          }
        });
      }
    }

    function render(p) {
      if (video) {
        if (!duration || video.seeking) return;
        var t = p * Math.max(duration - 0.04, 0);
        if (Math.abs(video.currentTime - t) > 0.015) video.currentTime = t;
      } else {
        drawFrame(p);
      }
    }

    function captions(p) {
      each(caps, function (c) {
        var a = (parseFloat(c.getAttribute('data-start')) || 0) / 100;
        var b = (parseFloat(c.getAttribute('data-end')) || 0) / 100;
        var on = p >= a && (p < b || (b >= 1 && p >= 1));
        if (c.classList.contains('is-on') !== on) c.classList.toggle('is-on', on);
      });
    }

    function frame() {
      raf = 0;
      var m = place();
      verify(m);
      target = m.p;
      var goal = hold ? clamp(target / (1 - hold), 0, 1) : target;
      if (current < 0) current = goal;
      current += (goal - current) * (1 - smooth);
      if (Math.abs(goal - current) < 0.0005) current = goal;
      render(current);
      captions(target);
      if (visible && current !== goal) raf = requestAnimationFrame(frame);
    }
    function tick() { if (!raf) raf = requestAnimationFrame(frame); }

    new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) load();
    }, { rootMargin: '150% 0px' }).observe(sec);

    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) tick();
    }).observe(sec);

    window.addEventListener('scroll', function () { place(); if (visible) tick(); }, { passive: true });
    window.addEventListener('resize', function () { place(); sizeCanvas(); tick(); });
    place();
    captions(0);
  }

  /* ---- 5. configurator on demand ---------------------------------------- */
  var configReady = false;
  function initConfigurator() {
    if (configReady) return;
    var shell = document.querySelector('[data-m16-config]');
    if (!shell) return;
    configReady = true;

    var mount = shell.querySelector('[data-m16-config-mount]');
    var nameEl = shell.querySelector('[data-m16-config-name]');
    var titleEl = shell.querySelector('[data-m16-config-title]');
    var switchEl = shell.querySelector('[data-m16-config-switch]');
    var backEl = shell.querySelector('[data-m16-config-back]');
    var VIEW = shell.getAttribute('data-view') || 'laptopconf';
    var LOADING = shell.getAttribute('data-loading-label') || '';
    var INTENT = 'm16-config-intent';
    var loadedSource = '';
    var pending = null;

    function store(v) { try { sessionStorage.setItem(INTENT, v); } catch (e) {} }
    function takeIntent() {
      try { var v = sessionStorage.getItem(INTENT); sessionStorage.removeItem(INTENT); return v; } catch (e) { return null; }
    }

    // Built like rn-config-loader.js: the configurator page's URL, plus the
    // preview theme id so previews fetch from the theme being previewed.
    function fragmentUrl(source) {
      var here = new URL(window.location.href);
      var url = new URL(source, window.location.origin);
      var preview = here.searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('view', VIEW);
      url.hash = '';
      return url.toString();
    }

    function runScripts(scripts) {
      return scripts.reduce(function (chain, original) {
        return chain.then(function () {
          var src = original.getAttribute('src');
          if (src && /jquery/i.test(src)) return;   // keep the page's own jQuery
          return new Promise(function (resolve) {
            var s = document.createElement('script');
            for (var i = 0; i < original.attributes.length; i++) {
              s.setAttribute(original.attributes[i].name, original.attributes[i].value);
            }
            if (src) {
              s.onload = resolve;
              s.onerror = resolve;
              document.body.appendChild(s);
            } else {
              s.text = original.textContent;
              document.body.appendChild(s);
              resolve();
            }
          });
        });
      }, Promise.resolve());
    }

    function load(source) {
      if (loadedSource === source) return Promise.resolve();
      if (pending) return pending;
      mount.setAttribute('aria-busy', 'true');
      document.documentElement.classList.add('m16-config-loading');

      pending = fetch(fragmentUrl(source), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('Configurator fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fragment = doc.querySelector('[data-rn-config-fragment]');
          if (!fragment) throw new Error('Configurator fragment missing');
          var scripts = Array.prototype.slice.call(fragment.querySelectorAll('script'));
          scripts.forEach(function (s) { s.parentNode.removeChild(s); });
          mount.appendChild(fragment);          // markup first, scripts second
          return runScripts(scripts);
        })
        .then(function () {
          loadedSource = source;
          pending = null;
          mount.setAttribute('aria-busy', 'false');
          document.documentElement.classList.remove('m16-config-loading');
        })
        .catch(function (err) {
          pending = null;
          if (window.console) console.error('[m16-config]', err);
          window.location.href = source;         // standalone configurator
        });
      return pending;
    }

    function otherOpener(source) {
      var all = document.querySelectorAll('[data-m16-config-open]');
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('data-source') !== source) return all[i];
      }
      return null;
    }

    function show(source, label, push) {
      return load(source).then(function () {
        if (loadedSource !== source) return;
        document.documentElement.classList.add('m16-configuring');
        shell.hidden = false;
        if (nameEl) nameEl.textContent = label || '';
        var other = otherOpener(source);
        if (switchEl) {
          if (other) {
            switchEl.textContent = (switchEl.getAttribute('data-prefix') || '') + ' ' + (other.getAttribute('data-label') || '');
            switchEl.setAttribute('data-source', other.getAttribute('data-source'));
            switchEl.setAttribute('data-label', other.getAttribute('data-label') || '');
            switchEl.hidden = false;
          } else {
            switchEl.hidden = true;
          }
        }
        if (push && window.history && history.pushState) {
          history.pushState({ m16Config: source }, '', window.location.pathname + window.location.search + '#configure');
        }
        window.scrollTo(0, 0);
        if (titleEl) titleEl.focus({ preventScroll: true });
      });
    }

    function hide(pop) {
      document.documentElement.classList.remove('m16-configuring');
      shell.hidden = true;
      if (!pop && window.history && history.state && history.state.m16Config) {
        history.back();
        return;
      }
      var target = document.getElementById('your-mech-16');
      if (target) target.scrollIntoView();
    }

    function open(source, label, button) {
      if (!source) return;
      if (loadedSource && loadedSource !== source) {
        // The configurator can only run once per page: remember the choice
        // and reload, and it opens on arrival.
        store(JSON.stringify({ source: source, label: label }));
        window.location.reload();
        return;
      }
      var original = '';
      if (button && LOADING && !loadedSource) {
        original = button.textContent;
        button.textContent = LOADING;
        button.setAttribute('aria-busy', 'true');
      }
      show(source, label, true).then(function () {
        if (button && original) {
          button.textContent = original;
          button.removeAttribute('aria-busy');
        }
      });
    }

    document.addEventListener('click', function (e) {
      var opener = e.target.closest ? e.target.closest('[data-m16-config-open], [data-m16-config-switch]') : null;
      if (!opener) return;
      e.preventDefault();
      open(opener.getAttribute('data-source') || opener.getAttribute('href'), opener.getAttribute('data-label') || '', opener);
    });

    if (backEl) backEl.addEventListener('click', function () { hide(false); });

    window.addEventListener('popstate', function () {
      if (document.documentElement.classList.contains('m16-configuring') && !(history.state && history.state.m16Config)) {
        hide(true);
      }
    });

    // Arrival after a platform switch. Only a stored choice from a real click
    // opens the configurator, never the URL on its own.
    var intent = takeIntent();
    if (intent) {
      try {
        var data = JSON.parse(intent);
        if (data && data.source) show(data.source, data.label, true);
      } catch (e) {}
    }
  }

  function initWithin(scope) {
    each(scope.querySelectorAll('.m16 [data-m16-tabs]'), initTabs);
    each(scope.querySelectorAll('.m16 [data-m16-gallery]'), initGallery);
    each(scope.querySelectorAll('.m16 [data-m16-sv]'), initScrollVideo);
  }
  initWithin(document);
  initConfigurator();

  /* ---- 6. Theme Editor --------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (e) { initWithin(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (el && el.closest && el.closest('[data-m16-gal-rail]')) {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }
  });
})();